import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Text } from '@fluentui/react';
import MessageCourier from '../../lib/MessageCourier';
import { useTranslation } from 'react-i18next';
import ProjectFolder from '../../system/projects/ProjectFolder';
import FileFolder, { FileTemporalityUpload } from '../FileDropzone/FileFolder';
import DocumentUploadFileListItem from './DocumentUploadFileListItem';
import { ITemporality } from '../../system/temporality/TemporalitySystem';
import TimeTaxSupportSystemClient from '../../system';
import { useEffect, useState } from 'react';
import Project from '../../system/projects/Project';

interface IDocumentControlDialog {
  showDialog: boolean;
  toggleShowDialog: () => void;
  system: TimeTaxSupportSystemClient;
  selectedFolder: ProjectFolder;
  fileFolder: FileFolder;
  courier: MessageCourier;
  isService: boolean;
  temporalityMonthly: ITemporality | undefined;
  forceReload: () => void;
  project: Project
}

export interface FileTemporality {
  file: FileTemporalityUpload;
  exercise: string;
  period: string;
  error?: {
    exercise?: string;
    period?: string;
  };
}

const DocumentUploadFilesTemporalityDialog = (props: IDocumentControlDialog) => {
  const { t } = useTranslation();

  const [files, setFiles] = useState<FileTemporality[]>([]);

  useEffect(() => {
    const allFiles: FileTemporalityUpload[] = [];
    props.fileFolder.collectAllFilesBelowInto(allFiles);
    
    if (allFiles.length) {
      setFiles(
        allFiles.map((file) => ({
          file: file,
          exercise: file.exercise || '',
          period: file.period || '',
        })),
      );
    }
  }, [props.fileFolder]);

  const updateFileTemporality = (index: number, changes: Partial<FileTemporality>) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...changes } : f)));
  };

  const validateFiles = (): boolean => {
    let valid = true;
    const newFiles = files.map((f) => {
      let error: { exercise?: string; period?: string } = {};
      if (props.isService) {
        if (!f.exercise.trim()) {
          error.exercise = t('Periodo requerido');
          valid = false;
        } else if (isNaN(Number(f.exercise))) {
          error.exercise = t('El ejercicio es invalido');
          valid = false;
        }
        if (!f.period) {
          error.period = t('Temporalidad requerida');
          valid = false;
        }
      }
      return { ...f, error: Object.keys(error).length ? error : undefined };
    });
    setFiles(newFiles);
    return valid;
  };

  const validateFileAtIndex = (index: number, field: 'exercise' | 'period') => {
    setFiles((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        let error = { ...f.error };
        if (props.isService) {
          if (field === 'exercise') {
            if (!f.exercise.trim()) {
              error.exercise = t('Periodo requerido');
            } else if (isNaN(Number(f.exercise))) {
              error.exercise = t('El ejercicio es invalido');
            } else {
              delete error.exercise;
            }
          }
          if (field === 'period') {
            if (!f.period) {
              error.period = t('Temporalidad requerida');
            } else {
              delete error.period;
            }
          }
        }
        return { ...f, error: Object.keys(error).length ? error : undefined };
      }),
    );
  };

  const saveFilesDrop = async (): Promise<void> => {
    const isValid = validateFiles();
    if (!isValid) return;
    props.toggleShowDialog();
    
    try {
      // Asignar exercise y period a los archivos originales antes de la subida
      files.forEach(({ file, exercise, period }) => {
        file.exercise = exercise;
        file.period = period;
      });


      await props.system.uploadFolderTo(props.fileFolder, props.selectedFolder!);
    } catch (e) {
      props.courier.messageError(e);
      props.system.getDocumentSystem().informEndPreparingUpload()
    } finally {
      props.selectedFolder!.resetChildren().finally(() => {
        props.forceReload();
      });
    }
  };

  return (
    <>
      <Dialog
        hidden={!props.showDialog}
        onDismiss={() => {
          props.toggleShowDialog();
          props.system.getDocumentSystem().informEndPreparingUpload();
        }}
        dialogContentProps={{
          type: DialogType.normal,
          title: t('Carga de documentos'),
        }}
        maxWidth={900}
        styles={{
          main: {
            minWidth: '95%',
          },
        }}
      >
        <div>
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12">
              <Text variant="mediumPlus">{props.isService ? t('Selecciona el periodo que sustentará cada documento') : t('Documentos listos para cargar.')}</Text>
            </div>
          </div>

          {files.map((file, i) => {
            return <DocumentUploadFileListItem key={i} file={file} temporalityMonthly={props.temporalityMonthly} onBlurField={(field) => validateFileAtIndex(i, field)} isService={props.isService} onChange={(changes) => updateFileTemporality(i, changes)} project={props.project} />;
          })}
        </div>
        <DialogFooter>
          <DefaultButton
            onClick={() => {
              props.toggleShowDialog();
              props.system.getDocumentSystem().informEndPreparingUpload();
            }}
            text={t('Cancelar')}
          />
          <PrimaryButton text={t('Cargar')} onClick={saveFilesDrop} iconProps={{ iconName: 'TextDocument' }} />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default DocumentUploadFilesTemporalityDialog;
